const TokenGenerator = require('./TokenGenerator');

const STORE_DOMAIN = 'arbitrip.com';
const SESSION_ID_STORAGE_KEY = 'riskified_session_id';

// The sid we hand the beacon on its script URL, and the cart_token we later report for the
// order — one value, which is what lets Riskified match the two. It cannot come from the
// beacon itself (RISKX only exists after the script we are building the URL for has loaded),
// so we mint it.
let sessionId = null;

// Set only by the injected script's own load event. Insertion succeeding proves nothing —
// an ad blocker or CSP rejects the request with the <script> tag sitting happily in the DOM,
// and then no load event ever fires.
let beaconLoaded = false;

function readStoredSessionId() {
    try {
        return window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    } catch (e) {
        return null;
    }
}

function storeSessionId(id) {
    try {
        window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, id);
    } catch (e) {
        // Ignore unavailable sessionStorage; the in-memory id still holds for this page.
    }
}

// Reused across full page reloads so the whole visit is one Riskified session: every load
// re-injects the beacon with the same sid.
function resolveSessionId() {
    const stored = readStoredSessionId();
    if (stored) {
        return stored;
    }
    const minted = TokenGenerator.generateSessionId();
    storeSessionId(minted);
    return minted;
}

// Null until the beacon has actually run, because only then is there behavioral data for
// Riskified to match this id against. Callers must omit cart_token entirely when it is null.
function getCartToken() {
    return beaconLoaded ? sessionId : null;
}

// Injects Riskified's beacon script once. Adapted from Riskified's documented snippet with
// one deliberate change: the docs' example always does
// `window.addEventListener('load', riskifiedBeaconLoad)`,
// assuming it's an inline <script> in the initial HTML (so the page's 'load' event hasn't
// fired yet). We call this from a React mount effect instead, where the 'load' event may
// have *already* fired by the time this runs — in which case a 'load' listener would
// silently never fire and the beacon would never load. Guarded by checking
// document.readyState first.
function riskifiedBeaconLoad() {
    try {
        sessionId = resolveSessionId();
        var url = 'https://beacon.riskified.com?shop=' + STORE_DOMAIN + '&sid=' + encodeURIComponent(sessionId);
        var s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.onload = function () {
            beaconLoaded = true;
        };
        s.src = url;
        var x = document.getElementsByTagName('script')[0];
        if (x && x.parentNode) {
            x.parentNode.insertBefore(s, x);
        } else {
            document.head.appendChild(s);
        }
    } catch (e) {
        // Never let injection failures affect the app. beaconLoaded stays false, so no
        // cart_token is reported for a beacon that never ran.
    }
}

let beaconLoadRequested = false;

// enabled is sourced from the per-company company.riskified_mode ('off' = disabled),
// exposed to the frontend via company.riskified_mode on the /profile response.
function loadBeacon(enabled) {
    if (!enabled) {
        return;
    }
    if (beaconLoadRequested) {
        return;
    }
    beaconLoadRequested = true;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    try {
        if (document.readyState === 'complete') {
            riskifiedBeaconLoad();
        } else {
            window.addEventListener('load', riskifiedBeaconLoad, false);
        }
    } catch (e) {
        // Never let injection failures affect the app.
    }
}

// Tells the already-loaded beacon about an SPA route change, so Riskified sees each
// client-side navigation as a distinct pageview (no full page reload to trigger one
// naturally). Must never throw — window.RISKX is third-party code we don't control, and
// this runs inside a React effect on every navigation, so an uncaught error here is not a
// quiet failure — it can affect app rendering, not just fraud-signal quality.
function trackPageview(pathname) {
    if (typeof window === 'undefined' || !window.RISKX || typeof window.RISKX.go !== 'function') {
        return;
    }
    try {
        window.RISKX.go(pathname);
    } catch (e) {
        // Never let a beacon-side failure break navigation/rendering.
    }
}

module.exports = {
    getCartToken,
    loadBeacon,
    trackPageview,
};
